import {
  CouponApiUnavailableError,
  fetchRakutenCoupons,
  type CouponApiOptions,
  type RakutenCoupon,
} from './api/rakutenCoupons';
import { createCouponCard } from './components/CouponCard';
import jullantsLogoUrl from './assets/jullants-logo.svg';

export interface CouponAppOptions extends CouponApiOptions {
  now?: () => Date;
}

type AppState = 'loading' | 'content' | 'empty' | 'unavailable' | 'error';

function textElement(tag: keyof HTMLElementTagNameMap, className: string, text: string): HTMLElement {
  const element = document.createElement(tag);
  element.className = className;
  element.textContent = text;
  return element;
}

function renderShell(root: HTMLElement): { content: HTMLElement; count: HTMLElement } {
  root.replaceChildren();
  const page = document.createElement('main');
  page.className = 'coupon-page';
  const header = document.createElement('header');
  header.className = 'coupon-header';
  const logo = document.createElement('img');
  logo.className = 'coupon-header__brand';
  logo.src = jullantsLogoUrl;
  logo.alt = 'JULLANTS';
  logo.width = 140;
  logo.height = 45;
  header.append(logo);
  header.append(textElement('p', 'coupon-header__store', 'RAKUTEN MARKET'));
  const heading = textElement('h1', 'coupon-header__title', '楽天市場店で\n今使えるクーポン');
  header.append(heading);
  const count = textElement('p', 'coupon-header__count', '');
  count.hidden = true;
  header.append(count);
  page.append(header);
  const content = document.createElement('section');
  content.className = 'coupon-content';
  page.append(content);
  const footer = textElement('footer', 'coupon-footer', 'JULLANTS OFFICIAL RAKUTEN STORE');
  page.append(footer);
  root.append(page);
  return { content, count };
}

function renderLoading(content: HTMLElement): void {
  content.dataset.state = 'loading';
  const loading = document.createElement('div');
  loading.className = 'state-panel state-panel--loading';
  loading.setAttribute('role', 'status');
  const petals = document.createElement('span');
  petals.className = 'loading-mark';
  petals.setAttribute('aria-hidden', 'true');
  loading.append(petals);
  loading.append(textElement('p', 'state-panel__title', 'クーポンを確認しています'));
  loading.append(textElement('p', 'state-panel__subtext', '少々お待ちください'));
  content.replaceChildren(loading);
}

function renderMessage(
  content: HTMLElement,
  state: Exclude<AppState, 'loading' | 'content'>,
  title: string,
  description: string,
  retry?: () => void,
): void {
  content.dataset.state = state;
  const panel = document.createElement('div');
  panel.className = `state-panel state-panel--${state}`;
  panel.append(textElement('p', 'state-panel__ornament', '✦'));
  panel.append(textElement('h2', 'state-panel__title', title));
  panel.append(textElement('p', 'state-panel__subtext', description));
  if (retry) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'state-panel__retry';
    button.textContent = 'もう一度確認する';
    button.addEventListener('click', retry, { once: true });
    panel.append(button);
  }
  content.replaceChildren(panel);
}

function renderCoupons(content: HTMLElement, coupons: RakutenCoupon[], now: Date): void {
  content.dataset.state = 'content';
  const list = document.createElement('div');
  list.className = 'coupon-list';
  for (const coupon of coupons) list.append(createCouponCard(coupon, now));
  content.replaceChildren(list);
}

export function createCouponApp(root: HTMLElement, options: CouponAppOptions) {
  let requestSequence = 0;
  const shell = renderShell(root);

  const load = async (): Promise<void> => {
    const sequence = ++requestSequence;
    shell.count.hidden = true;
    renderLoading(shell.content);
    try {
      const coupons = await fetchRakutenCoupons(options);
      if (sequence !== requestSequence) return;
      if (coupons.length === 0) {
        renderMessage(
          shell.content,
          'empty',
          '現在ご利用いただける\nクーポンはありません。',
          '新しいキャンペーンを\nお楽しみに。',
        );
        return;
      }
      shell.count.textContent = `${coupons.length} COUPONS AVAILABLE`;
      shell.count.hidden = false;
      renderCoupons(shell.content, coupons, options.now?.() ?? new Date());
    } catch (error) {
      if (sequence !== requestSequence) return;
      if (error instanceof CouponApiUnavailableError) {
        renderMessage(
          shell.content,
          'unavailable',
          '現在クーポン情報を\n確認できません。',
          '時間をおいて、\nもう一度お試しください。',
          () => void load(),
        );
        return;
      }
      renderMessage(
        shell.content,
        'error',
        'クーポン情報を取得できませんでした。',
        '通信環境をご確認のうえ、\nもう一度お試しください。',
        () => void load(),
      );
    }
  };

  return Object.freeze({ load });
}
